// src/App.js
import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
// ReportingProvider removed — reporting accessed through portal
import { SyncProvider } from './context/SyncContext';
import { ThemeProvider } from './context/ThemeContext';
import host from './host';
import PosAppShell from './pos-app/PosAppShell';
import PortalAppShell from './portal-app/PortalAppShell';
import { isPortalFlavor } from './appFlavor';

// POS Pages
import SalesPage from './pages/pos/CashierSalesPage';
import OrderViewPage from './pages/pos/OrderViewPage';
import WaiterSalesPage from './pages/pos/WaiterSalesPage';
import TableServicePage from './pages/pos/TableServicePage';
import FunctionsPage from './pages/pos/FunctionsPage';

// Auth Pages
import LockScreenPage from './pages/auth/LockScreenPage';

// Super Admin Pages
import SuperAdminPage from './pages/superadmin/SuperAdminPage';
import SuperAdminLoginPage from './pages/superadmin/SuperAdminLoginPage';

// Portal Pages
import OverviewPage from './pages/portal/OverviewPage';
import PortalEmployeesPage from './pages/portal/PortalEmployeesPage';
import PortalProductsPage from './pages/portal/PortalProductsPage';
import PortalExpensesPage from './pages/portal/PortalExpensesPage';
import PortalReportingPage from './pages/portal/reporting/PortalReportingPage';
import PortalSettingsPage from './pages/portal/PortalSettingsPage';
import MarketingPage from './pages/portal/MarketingPage';
import PortalRolesPage from './pages/portal/PortalRolesPage';
import PortalSchedulePage from './pages/portal/PortalSchedulePage';
import PortalEmployeeDetailPage from './pages/portal/employee-detail/PortalEmployeeDetailPage';
import PortalPayRunPage from './pages/portal/payrun/PortalPayRunPage';
import PortalPayRunDetailPage from './pages/portal/payrun/PortalPayRunDetailPage';
import PortalInventoryPage from './pages/portal/PortalInventoryPage';
import PortalEodPage from './pages/portal/reporting/PortalEodPage';
import PortalVoidsPage from './pages/portal/PortalVoidsPage';
import PortalRefundsPage from './pages/portal/PortalRefundsPage';
import PortalRecentTransactionsPage from './pages/portal/PortalRecentTransactionsPage';
import PortalDevicesPage from './pages/portal/PortalDevicesPage';
import PortalHelpCenterPage from './pages/portal/PortalHelpCenterPage';
import PortalReservationsPage from './pages/portal/PortalReservationsPage';

// Landing Pages
import LoginPage from './pages/auth/LoginPage';
import LandingPage from './pages/landing/HomeLandingPage';
import HowItWorksPage from './pages/landing/HowItWorksPage';
import OurServicesPage from './pages/landing/OurServicesPage';
import FAQPage from './pages/landing/FAQPage';
import ContactUsPage from './pages/landing/ContactUsPage';
import AboutUsPage from './pages/landing/AboutUsPage';
import PrivacyPolicyPage from './pages/landing/PrivacyPolicyPage';
import TermsOfServicePage from './pages/landing/TermsOfServicePage';
import DataDeletionPage from './pages/landing/DataDeletionPage';

// Components
import Layout from './components/common/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import { LanguageProvider } from './context/LanguageContext';



const AppRoutes = () => {
    const { isAuthenticated, isLoading, userRole, authType, selectedPortal, hasPermission, companyId, tenantId } = useAuth();

    if (isLoading) {
        console.log('⏳ Still loading...');
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        console.log(" User not authenticated, redirecting to login");
        return <Navigate to="/login" replace />;
    }

    // Guard: non-super-admin users cannot access /super-admin routes
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/super-admin') && userRole !== 'super_admin') {
        console.log(" Non-super-admin attempting to access /super-admin, redirecting to login");
        return <Navigate to="/login" replace />;
    }

    console.log(' User authenticated, rendering routes');

    // Unreachable on every shipping host: Capacitor apps never reach AppRoutes
    // (they render PosAppShell/PortalAppShell instead), and the browser always
    // has host.isPosDevice === false. Left in place from the Electron era.
    if (host.isPosDevice) {
        return (
            <Routes>
                <Route path="functions" element={<FunctionsPage />} />
                <Route path="functions/cashier-sales" element={
                    <Layout><SalesPage /></Layout>
                } />
                <Route path="functions/lock" element={<LockScreenPage />} />
                <Route path="functions/orders/:category" element={<OrderViewPage />} />
                <Route path="*" element={<Navigate to="/functions" replace />} />
            </Routes>
        );
    }

    // Super-admin routing — render page directly, no nested route matching needed
    if (userRole === 'super_admin') {
        return <SuperAdminPage />;
    }

    // Manager Portal routing — any authenticated user who selected the manager portal
    if (selectedPortal === 'manager') {
        return (
            <Routes>
                <Route path="portal" element={<OverviewPage />} />
                <Route path="portal/overview" element={<OverviewPage />} />
                <Route path="portal/employees" element={<PortalEmployeesPage />} />
                <Route path="portal/employees/:employeeId" element={<PortalEmployeeDetailPage />} />
                <Route path="portal/products" element={<PortalProductsPage />} />
                <Route path="portal/expenses" element={<PortalExpensesPage />} />
                <Route path="portal/transactions" element={<PortalRecentTransactionsPage />} />
                <Route path="portal/reporting" element={<PortalReportingPage />} />
                <Route path="portal/settings" element={<PortalSettingsPage />} />
                <Route path="portal/marketing" element={<MarketingPage />} />
                <Route path="portal/roles" element={<PortalRolesPage />} />
                <Route path="portal/schedule" element={<PortalSchedulePage />} />
                <Route path="portal/payrun" element={<PortalPayRunPage />} />
                <Route path="portal/payrun/:payRunId" element={<PortalPayRunDetailPage />} />
                <Route path="portal/payroll" element={<Navigate to="/portal/payrun" replace />} />
                <Route path="portal/inventory" element={<PortalInventoryPage />} />
                <Route path="portal/end-of-day" element={<PortalEodPage />} />
                <Route path="portal/voids" element={<PortalVoidsPage />} />
                <Route path="portal/refunds" element={<PortalRefundsPage />} />
                <Route path="portal/devices" element={<PortalDevicesPage />} />
                <Route path="portal/reservations" element={<PortalReservationsPage />} />
                <Route path="portal/help-center" element={<PortalHelpCenterPage />} />
                <Route path="*" element={<Navigate to="/portal/overview" replace />} />
            </Routes>
        );
    }

    // Function-specific routing (NO reporting here anymore)
    if (authType === 'function') {
        return (
            <Routes>
                <Route path="functions" element={<FunctionsPage />} />
                <Route path="functions/cashier-sales" element={
                    <Layout>
                        <SalesPage />
                    </Layout>
                } />
                <Route path="functions/waiter-sales" element={
                    <Layout>
                        <WaiterSalesPage />
                    </Layout>
                } />
                <Route path="functions/table-service" element={
                    <TableServicePage />
                } />
                <Route path="functions/lock" element={<LockScreenPage />} />
                <Route path="functions/orders/:category" element={<OrderViewPage />} />
                <Route path="*" element={<Navigate to="/functions" replace />} />
            </Routes>
        );
    }

    if (authType === 'direct') {
        return (
            <Routes>
                <Route path="functions/orders/:category" element={<OrderViewPage />} />
                <Route path="*" element={<Navigate to="/functions" replace />} />
            </Routes>
        );
    }

    // Default authenticated state - show dashboard (NO reporting here anymore)
    return (
        <Routes>
            <Route path="functions" element={<FunctionsPage />} />
            <Route path="functions/cashier-sales" element={
                <Layout>
                    <SalesPage />
                </Layout>
            } />
            <Route path="functions/waiter-sales" element={
                <Layout>
                    <WaiterSalesPage />
                </Layout>
            } />
            <Route path="functions/table-service" element={
                <TableServicePage />
            } />
            <Route path="functions/lock" element={<LockScreenPage />} />
            <Route path="functions/orders/:category" element={<OrderViewPage />} />
            <Route path="*" element={<Navigate to="/functions" replace />} />
        </Routes>
    );
};

function App() {
    // Use HashRouter for Electron (file:// protocol), BrowserRouter for web
    const Router = host.usesHashRouter ? HashRouter : BrowserRouter;

    return (
        <ThemeProvider>
        <LanguageProvider>
            <AuthProvider>
                <Router>
                    {host.type === 'capacitor' ? (isPortalFlavor ? <PortalAppShell /> : <PosAppShell />) : (
                        // SyncProvider is the browser surface's connection provider (polls GET /health).
                        // The native POS shell (PosAppShell) mounts its own ConnectionProvider,
                        // so wrapping the whole app here would double the health-polling traffic.
                        <SyncProvider>
                            <Routes>
                                <Route path="/" element={<LandingPage />} />
                                <Route path="/how-it-works" element={<HowItWorksPage />} />
                                <Route path="/our-services" element={<OurServicesPage />} />
                                <Route path="/about-us" element={<AboutUsPage />} />
                                <Route path="/faq" element={<FAQPage />} />
                                <Route path="/contact-us" element={<ContactUsPage />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                                <Route path="/data-deletion" element={<DataDeletionPage />} />
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/system-access" element={<SuperAdminLoginPage />} />
                                <Route path="/*" element={<AppRoutes />} />
                            </Routes>
                        </SyncProvider>
                    )}
                </Router>
            </AuthProvider>
        </LanguageProvider>
        </ThemeProvider>
    );
}

export default App;